import { useState } from 'react';
import { ShieldCheck, ShieldOff, X, Icon } from '../ui/Icon';
import { authApi } from '../../api/auth.api';
import { getAccessToken } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import Spinner from '../ui/Spinner';
import { useTranslation } from 'react-i18next';

interface Props {
  onClose: () => void;
}

type Step = 'menu' | 'setup' | 'disable';

export default function TwoFactorModal({ onClose }: Props) {
  const { user, setAuth } = useAuthStore();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('menu');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');

  const enabled = user?.twoFactorEnabled ?? false;

  async function startSetup() {
    setLoading(true); setError('');
    try {
      const data = await authApi.setup2FA();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('setup');
    } catch {
      setError(t('auth.twoFactor.setupError'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmEnable() {
    setLoading(true); setError('');
    try {
      await authApi.enable2FA({ secret, code });
      const fresh = await authApi.me();
      const token = getAccessToken()!;
      setAuth(fresh, token);
      onClose();
    } catch {
      setError(t('auth.twoFactor.invalidCode'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDisable() {
    setLoading(true); setError('');
    try {
      await authApi.disable2FA({ code });
      const fresh = await authApi.me();
      const token = getAccessToken()!;
      setAuth(fresh, token);
      onClose();
    } catch {
      setError(t('auth.twoFactor.invalidCode'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <Icon as={ShieldCheck} size={20} className="text-teal-600 dark:text-teal-400" />
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">{t('auth.twoFactor.modalTitle')}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            <Icon as={X} size={20} />
          </button>
        </div>

        <div className="px-6 py-5">
          {step === 'menu' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {enabled ? t('auth.twoFactor.statusEnabled') : t('auth.twoFactor.statusDisabled')}
              </p>
              {enabled ? (
                <button
                  onClick={() => { setStep('disable'); setCode(''); setError(''); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors border border-red-200 dark:border-red-800"
                >
                  <Icon as={ShieldOff} size={16} />
                  {t('auth.twoFactor.disable')}
                </button>
              ) : (
                <button
                  onClick={startSetup}
                  disabled={loading}
                  className="btn-primary w-full gap-2"
                >
                  {loading ? <Spinner size="sm" /> : <><Icon as={ShieldCheck} size={16} />{t('auth.twoFactor.enable')}</>}
                </button>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          )}

          {step === 'setup' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('auth.twoFactor.scanQr')}</p>
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-48 h-48 rounded-xl border border-slate-200 dark:border-slate-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('auth.twoFactor.manualEntry')}</p>
                <code className="block text-xs bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 font-mono text-slate-700 dark:text-slate-300 break-all select-all">{secret}</code>
              </div>
              <div>
                <label className="label">{t('auth.twoFactor.codeLabel')}</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  className="input text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus autoComplete="one-time-code"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => { setStep('menu'); setError(''); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
                <button
                  onClick={confirmEnable}
                  disabled={loading || code.length < 6}
                  className="btn-primary flex-1"
                >
                  {loading ? <Spinner size="sm" /> : t('auth.twoFactor.confirm')}
                </button>
              </div>
            </div>
          )}

          {step === 'disable' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('auth.twoFactor.disablePrompt')}</p>
              <div>
                <label className="label">{t('auth.twoFactor.codeLabel')}</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  className="input text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus autoComplete="one-time-code"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => { setStep('menu'); setError(''); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
                <button
                  onClick={confirmDisable}
                  disabled={loading || code.length < 6}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  {loading ? <Spinner size="sm" /> : t('auth.twoFactor.confirmDisable')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
